import { Fragment, useEffect, useMemo, useState } from "react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Search, Pencil, Trash2, Wrench, Image as ImageIcon, ChevronDown, ChevronRight, Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import FileUploader from "@/components/FileUploader";

const MAINTENANCE_TYPES = [
  { value: "service",    label: "Service" },
  { value: "repair",     label: "Repair" },
  { value: "mot",        label: "MOT" },
  { value: "tax",        label: "Road tax" },
  { value: "tyres",      label: "Tyres" },
  { value: "brakes",     label: "Brakes" },
  { value: "inspection", label: "Inspection" },
  { value: "breakdown",  label: "Breakdown / Recovery" },
  { value: "other",      label: "Other" },
];
const MTYPE_LABEL = Object.fromEntries(MAINTENANCE_TYPES.map(t => [t.value, t.label]));

const M_EMPTY = {
  vehicle_id: "", date: new Date().toISOString().slice(0, 10),
  type: "service", description: "",
  mileage: "", supplier_id: "", supplier_snapshot: {}, garage: "",
  cost: "", invoice_number: "",
  next_due_date: "", notes: "",
};

export default function MaintenanceRecords() {
  const [records, setRecords] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filterVehicle, setFilterVehicle] = useState("__all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState(M_EMPTY);
  const [saving, setSaving] = useState(false);

  const vehById = useMemo(() => Object.fromEntries(vehicles.map(v => [v.id, v])), [vehicles]);
  const supById = useMemo(() => Object.fromEntries(suppliers.map(s => [s.id, s])), [suppliers]);

  const load = () => Promise.all([
    api.get("/maintenance-records").then(r => setRecords(r.data || [])),
    api.get("/vehicles").then(r => setVehicles(r.data || [])),
    api.get("/maintenance-suppliers").then(r => setSuppliers(r.data || [])),
  ]).catch(e => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = records;
    if (filterVehicle && filterVehicle !== "__all") list = list.filter(r => r.vehicle_id === filterVehicle);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(r => {
        const v = vehById[r.vehicle_id];
        return [r.description, r.garage, r.invoice_number, r.notes,
                MTYPE_LABEL[r.type], v?.registration, v?.make, v?.model]
          .filter(Boolean).some(s => s.toLowerCase().includes(q));
      });
    }
    return list;
  }, [records, filterVehicle, search, vehById]);

  const totalCost = useMemo(() => filtered.reduce((s, r) => s + (Number(r.cost) || 0), 0), [filtered]);

  const startNew = () => {
    const def = (filterVehicle !== "__all" && filterVehicle) || "";
    setEditing(null); setForm({ ...M_EMPTY, vehicle_id: def }); setOpen(true);
  };
  const startEdit = (r) => {
    setEditing(r);
    setForm({
      vehicle_id: r.vehicle_id || "",
      date: r.date || new Date().toISOString().slice(0, 10),
      type: r.type || "service",
      description: r.description || "",
      mileage: r.mileage ?? "",
      supplier_id: r.supplier_id || "",
      supplier_snapshot: r.supplier_snapshot || {},
      garage: r.garage || "",
      cost: r.cost ?? "",
      invoice_number: r.invoice_number || "",
      next_due_date: r.next_due_date || "",
      notes: r.notes || "",
    });
    setOpen(true);
  };
  const onClose = () => { setOpen(false); setEditing(null); setForm(M_EMPTY); };

  const save = async () => {
    if (!form.vehicle_id) { toast.error("Pick a vehicle"); return; }
    if (!form.date) { toast.error("Date is required"); return; }
    const payload = {
      ...form,
      mileage: form.mileage === "" || form.mileage === null ? null : Number(form.mileage),
      cost:    form.cost === "" || form.cost === null ? 0 : Number(form.cost),
    };
    setSaving(true);
    try {
      if (editing) await api.put(`/maintenance-records/${editing.id}`, payload);
      else         await api.post("/maintenance-records", payload);
      toast.success(editing ? "Record updated" : "Record added");
      onClose();
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm("Delete this maintenance record?")) return;
    try { await api.delete(`/maintenance-records/${r.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = (id) => setExpandedId(curr => curr === id ? null : id);

  const registerQuery = useMemo(() => {
    const p = new URLSearchParams();
    if (filterVehicle && filterVehicle !== "__all") p.set("vehicle_id", filterVehicle);
    const s = p.toString();
    return s ? `?${s}` : "";
  }, [filterVehicle]);

  return (
    <div data-testid="maintenance-page" className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Fleet · Maintenance</div>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">Maintenance records</h1>
          <p className="text-sm text-slate-600 mt-1">Service, MOT, tyres, repairs and breakdowns logged against each vehicle. Filter by vehicle and export the register, or download a single record as a PDF.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" data-testid="maint-register-pdf" className="rounded-sm border-slate-300">
            <a href={fileUrl(`/maintenance-records/export.pdf${registerQuery}`)} download rel="noopener">
              <Download className="w-4 h-4 mr-1.5" /> Register PDF
            </a>
          </Button>
          <Button asChild variant="outline" data-testid="maint-register-xlsx" className="rounded-sm border-slate-300">
            <a href={fileUrl(`/maintenance-records/export.xlsx${registerQuery}`)} download rel="noopener">
              <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Register Excel
            </a>
          </Button>
          <Button data-testid="maint-add-btn" onClick={startNew} disabled={vehicles.length === 0} className="rounded-sm bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300">
            <Plus className="w-4 h-4 mr-1.5" /> Add record
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="maint-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search description, garage, invoice…" className="pl-9 rounded-sm border-slate-300" />
        </div>
        <div className="min-w-[260px]">
          <Select value={filterVehicle} onValueChange={setFilterVehicle}>
            <SelectTrigger data-testid="maint-filter-vehicle" className="rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All vehicles</SelectItem>
              {vehicles.map(v => (
                <SelectItem key={v.id} value={v.id}>{v.registration} · {[v.make, v.model].filter(Boolean).join(" ") || "—"}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-slate-500 tabular-nums ml-auto">
          {filtered.length} record{filtered.length === 1 ? "" : "s"} · total £{totalCost.toFixed(2)}
        </div>
      </div>

      <Card className="rounded-sm border border-slate-200 shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="maintenance-table">
            <thead className="bg-slate-900 text-white">
              <tr>
                <Th className="w-8"></Th>
                <Th>Date</Th>
                <Th>Vehicle</Th>
                <Th>Type</Th>
                <Th>Description</Th>
                <Th>Garage</Th>
                <Th>Mileage</Th>
                <Th>Cost</Th>
                <Th>Next due</Th>
                <Th className="text-right pr-4">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-slate-500 text-sm">
                    <Wrench className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    {records.length === 0 ? "No maintenance recorded yet." : "No records match your filters."}
                  </td>
                </tr>
              )}
              {filtered.map((r) => {
                const v = vehById[r.vehicle_id];
                const isOpen = expandedId === r.id;
                return (
                  <Fragment key={r.id}>
                  <tr className="border-t border-slate-200 hover:bg-slate-50">
                    <Td className="pr-0">
                      <button data-testid={`maint-expand-${r.id}`} onClick={() => toggle(r.id)} className="h-7 w-7 inline-flex items-center justify-center rounded-sm hover:bg-slate-200 text-slate-600" title={isOpen ? "Hide receipts" : "Show receipts"}>
                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                    </Td>
                    <Td><div className="tabular-nums text-slate-900">{r.date || "—"}</div></Td>
                    <Td>
                      <div className="font-medium text-slate-900 tracking-wider">{v?.registration || "—"}</div>
                      <div className="text-xs text-slate-500">{[v?.make, v?.model].filter(Boolean).join(" ")}</div>
                    </Td>
                    <Td><Badge variant="outline" className="rounded-sm">{MTYPE_LABEL[r.type] || r.type}</Badge></Td>
                    <Td><div className="text-slate-900 max-w-xs">{r.description || "—"}</div></Td>
                    <Td>
                      <div className="text-slate-700">{r.garage || (r.supplier_snapshot && r.supplier_snapshot.name) || "—"}</div>
                      {r.supplier_snapshot && r.supplier_snapshot.postcode && (
                        <div className="text-xs text-slate-500">{r.supplier_snapshot.postcode}</div>
                      )}
                    </Td>
                    <Td><div className="tabular-nums text-slate-700">{r.mileage ? r.mileage.toLocaleString() : "—"}</div></Td>
                    <Td><div className="tabular-nums text-slate-900">£{Number(r.cost || 0).toFixed(2)}</div></Td>
                    <Td><div className="tabular-nums text-slate-700">{r.next_due_date || "—"}</div></Td>
                    <Td className="text-right pr-4">
                      <Button asChild size="sm" variant="ghost" data-testid={`maint-pdf-${r.id}`} className="rounded-sm h-8 w-8 p-0" title="Download record PDF">
                        <a href={fileUrl(`/maintenance-records/${r.id}/pdf`)} download rel="noopener">
                          <Download className="w-4 h-4" />
                        </a>
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`maint-receipts-${r.id}`} onClick={() => toggle(r.id)} className="rounded-sm h-8 w-8 p-0" title="Receipts & docs">
                        <ImageIcon className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`maint-edit-${r.id}`} onClick={() => startEdit(r)} className="rounded-sm h-8 w-8 p-0">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`maint-delete-${r.id}`} onClick={() => remove(r)} className="rounded-sm h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </Td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-slate-50/70 border-t border-slate-200">
                      <td colSpan={10} className="px-6 py-5">
                        <FileUploader entityType="maintenance" entityId={r.id} label={`Receipts & documents — ${v?.registration || ""} ${r.date}`} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent data-testid="maintenance-dialog" className="rounded-sm sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editing ? "Edit maintenance record" : "Add maintenance record"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <Field label="Vehicle *">
              <Select value={form.vehicle_id} onValueChange={(v) => setForm({ ...form, vehicle_id: v })}>
                <SelectTrigger data-testid="maint-vehicle" className="rounded-sm border-slate-300"><SelectValue placeholder="Pick a vehicle" /></SelectTrigger>
                <SelectContent>
                  {vehicles.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.registration} · {[v.make, v.model].filter(Boolean).join(" ") || "—"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Date *">
              <Input data-testid="maint-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm border-slate-300" />
            </Field>
            <Field label="Type">
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="maint-type" className="rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MAINTENANCE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Cost (£)">
              <Input data-testid="maint-cost" type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} placeholder="0.00" className="rounded-sm border-slate-300" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Garage / provider">
                <Select
                  value={form.supplier_id || "__manual"}
                  onValueChange={(v) => {
                    if (v === "__manual") {
                      setForm({ ...form, supplier_id: "", supplier_snapshot: {} });
                    } else {
                      const sup = supById[v];
                      setForm({
                        ...form,
                        supplier_id: v,
                        supplier_snapshot: sup || {},
                        garage: sup?.name || form.garage || "",
                      });
                    }
                  }}
                >
                  <SelectTrigger data-testid="maint-supplier" className="rounded-sm border-slate-300">
                    <SelectValue placeholder="Pick a saved supplier" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__manual">— Manual entry (no saved supplier) —</SelectItem>
                    {suppliers.filter(s => s.is_active !== false).map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}{s.postcode ? ` · ${s.postcode}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.supplier_id ? (
                  <div className="mt-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-2 space-y-0.5" data-testid="maint-supplier-preview">
                    {(() => {
                      const s = supById[form.supplier_id] || form.supplier_snapshot || {};
                      const addr = [s.address_line1, s.address_line2, s.city, s.postcode, s.country].filter(Boolean).join(", ");
                      return (
                        <>
                          <div className="font-medium text-slate-900">{s.name || "—"}</div>
                          {s.contact_name && <div>{s.contact_name}</div>}
                          {(s.phone || s.email) && <div className="tabular-nums">{[s.phone, s.email].filter(Boolean).join(" · ")}</div>}
                          {addr && <div>{addr}</div>}
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <Input
                    data-testid="maint-garage-manual"
                    value={form.garage}
                    onChange={(e) => setForm({ ...form, garage: e.target.value })}
                    placeholder="Type a one-off garage name…"
                    className="mt-2 rounded-sm border-slate-300"
                  />
                )}
              </Field>
            </div>
            <Field label="Mileage">
              <Input data-testid="maint-mileage" type="number" inputMode="numeric" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} placeholder="miles" className="rounded-sm border-slate-300" />
            </Field>
            <Field label="Invoice number">
              <Input data-testid="maint-invoice" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} placeholder="INV-…" className="rounded-sm border-slate-300" />
            </Field>
            <Field label="Next due date">
              <Input data-testid="maint-next" type="date" value={form.next_due_date} onChange={(e) => setForm({ ...form, next_due_date: e.target.value })} className="rounded-sm border-slate-300" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Description / work done">
                <Textarea data-testid="maint-desc" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Full service, oil + filters" className="rounded-sm border-slate-300" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Notes">
                <Textarea data-testid="maint-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any extra notes…" className="rounded-sm border-slate-300" />
              </Field>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
            <Button data-testid="maint-save" onClick={save} disabled={saving} className="rounded-sm bg-slate-900 hover:bg-slate-800">
              {saving ? "Saving…" : (editing ? "Save changes" : "Add record")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Th({ children, className = "" }) { return <th className={`text-left text-[11px] uppercase tracking-wider font-semibold px-3 py-2.5 ${className}`}>{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-3 align-top ${className}`}>{children}</td>; }
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
